/**
 * City Night - wide skyline with slow traffic streaks.
 *
 * Fully periodic: every animated term completes an integer number of
 * cycles per loop, so the last frame matches the first.
 */

import { createRng, drawGrain, drawVignette, glow, loopPulse, reactive, withAlpha } from './shared.js';

const WORLD = (() => {
  const rng = createRng(0x51c17);
  const buildings = [];
  let x = -0.02;
  while (x < 1.02) {
    const width = 0.045 + rng() * 0.075;
    const height = 0.16 + rng() * 0.34;
    const windows = [];
    const cols = Math.max(2, Math.round(width * 60));
    const rows = Math.max(3, Math.round(height * 46));
    for (let c = 0; c < cols; c += 1) {
      for (let r = 0; r < rows; r += 1) {
        if (rng() > 0.46) continue;
        windows.push({
          nx: (c + 0.5) / cols,
          ny: (r + 0.5) / rows,
          cycles: 1 + Math.floor(rng() * 3),
          offset: rng(),
          warm: rng(),
        });
      }
    }
    buildings.push({ nx: x, width, height, windows, depth: rng() });
    x += width + 0.004 + rng() * 0.012;
  }

  const streaks = Array.from({ length: 26 }, () => ({
    ny: 0.72 + rng() * 0.24,
    speed: 1 + Math.floor(rng() * 3),
    offset: rng(),
    length: 0.05 + rng() * 0.16,
    warm: rng(),
    alpha: 0.35 + rng() * 0.5,
  }));

  return { buildings, streaks };
})();

export default {
  id: 'city-night',
  label: 'City Night',
  description: 'Procedural skyline with flickering windows and traffic light streaks.',
  loopable: true,

  draw(ctx, { t, loopSeconds, w, h, audio, strength }) {
    const phase = (t % loopSeconds) / loopSeconds;
    const bass = reactive(audio.bass, strength);
    const level = reactive(audio.level, strength);

    // --- sky
    const sky = ctx.createLinearGradient(0, 0, 0, h);
    sky.addColorStop(0, '#070A12');
    sky.addColorStop(0.45, '#101725');
    sky.addColorStop(0.72, '#1A1F2E');
    sky.addColorStop(1, '#0B0D13');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h);

    // --- atmospheric haze behind the skyline
    glow(ctx, w * 0.5, h * 0.7, h * 0.42, '#2A3550', 0.55 + bass * 0.2);
    glow(ctx, w * 0.22, h * 0.62, h * 0.26, '#3A2A1C', 0.3 + bass * 0.15);

    // --- buildings
    for (const b of WORLD.buildings) {
      const bx = b.nx * w;
      const bw = b.width * w;
      const bh = b.height * h;
      const by = h * 0.78 - bh;
      const shade = 6 + Math.round(b.depth * 14);
      ctx.fillStyle = `rgb(${shade},${shade + 2},${shade + 7})`;
      ctx.fillRect(bx, by, bw, bh);

      for (const win of b.windows) {
        const flicker = 0.35 + 0.65 * loopPulse(phase + win.offset, win.cycles);
        const warm = win.warm;
        const alpha = (0.18 + flicker * 0.55) * (0.85 + bass * 0.3);
        const color = warm > 0.6 ? '#FFD9A0' : warm > 0.3 ? '#FFE9C4' : '#BFD4FF';
        ctx.fillStyle = withAlpha(color, alpha);
        const wx = bx + win.nx * bw;
        const wy = by + win.ny * bh;
        ctx.fillRect(wx, wy, Math.max(1, bw * 0.035), Math.max(1, bh * 0.012));
      }
    }

    // --- light pollution above the rooftops
    glow(ctx, w * 0.5, h * 0.78, h * 0.3, '#FF9A4D', 0.12 + level * 0.08);

    // --- road / traffic streaks
    const roadTop = h * 0.78;
    const road = ctx.createLinearGradient(0, roadTop, 0, h);
    road.addColorStop(0, '#0A0C12');
    road.addColorStop(0.35, '#141821');
    road.addColorStop(1, '#080A0E');
    ctx.fillStyle = road;
    ctx.fillRect(0, roadTop, w, h - roadTop);

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, roadTop, w, h - roadTop);
    ctx.clip();
    for (const s of WORLD.streaks) {
      const travel = (s.offset + phase * s.speed) % 1;
      const x = travel * (1 + s.length) - s.length;
      const y = s.ny * h;
      const len = s.length * w;
      const color = s.warm > 0.5 ? '#FFB066' : '#CFE0FF';
      const gradient = ctx.createLinearGradient(x * w, 0, (x + s.length) * w, 0);
      gradient.addColorStop(0, withAlpha(color, 0));
      gradient.addColorStop(0.5, withAlpha(color, s.alpha * (0.75 + level * 0.4)));
      gradient.addColorStop(1, withAlpha(color, 0));
      ctx.fillStyle = gradient;
      ctx.fillRect(x * w, y, len, Math.max(1.5, h * 0.0035));
    }
    ctx.restore();

    // --- foreground haze + finish
    glow(ctx, w * 0.5, h * 0.96, h * 0.2, '#1B2434', 0.4);
    drawVignette(ctx, w, h, 0.5 + bass * 0.08);
    drawGrain(ctx, w, h, { t, alpha: 0.03 });
  },
};
